import { Ionicons } from "@expo/vector-icons";
import { classValidatorResolver } from "@hookform/resolvers/class-validator";
import { Link, router, useFocusEffect } from "expo-router";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { View, Text, ScrollView } from "react-native";
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  Easing,
} from "react-native-reanimated";
import { useDispatch } from "react-redux";
import { v1 as uuidv1 } from "uuid";

import TransferCartoon from "@/assets/transfercartoon.svg";
import Subtitle from "@/components/Subtitle";
import Title from "@/components/Title";
import Button from "@/components/button/Button";
import InputChoose from "@/components/input/InputChoose";
import InputDropList from "@/components/input/InputDropList";
import WarningModal from "@/components/modal/utils/WarningModal";
import { setForm } from "@/context/actions/actions";
import { sendRequest } from "@/utils/ApiUtil";
import { RegisterCounterpartyRequest } from "@/utils/dto/accounts.request";
import { WithdrawalType } from "@/utils/dto/extracted_enums";
import {
  allJurisdictions,
  ibanJurisdictions,
  accountNumberJurisdictions,
  clabeJurisdictions,
} from "@/utils/transactions/Jurisdictions";

type FormData = {
  withdrawalType: string;
  countryCode: string;
};

interface CountryData {
  country: string;
  translation: string;
  code: string;
  svg: string;
  idd: string;
}

export default function Register() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState("");
  const [withdrawalType, setWithdrawalType] = useState("");
  const [isNotAvailable, setIsNotAvailable] = useState(false);
  const [showIsNotAvailableModal, setShowIsNotAvailableModal] = useState(false);
  const [countries, setCountries] = useState<
    { value: string; code: string; uniqueKey: string }[]
  >([]);
  const [selectedCountry, setSelectedCountry] = useState("");

  const resolver = classValidatorResolver(RegisterCounterpartyRequest, {
    validator: { skipMissingProperties: true },
  });

  const { control, handleSubmit, reset, setValue } = useForm<FormData>({
    mode: "onSubmit",
    resolver,
  });

  const fiatOptionsOpacity = useSharedValue(0);

  useFocusEffect(
    React.useCallback(() => {
      reset();
      setSelectedCountry("");
      setWithdrawalType("");
      setIsNotAvailable(false);
      setShowIsNotAvailableModal(false);
      fiatOptionsOpacity.value = 0;
      return () => {};
    }, []),
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await sendRequest("get", "/countries", {
          searchTerm,
        });
        const mappedCountries = response.map((country: CountryData) => ({
          value: country.country,
          code: country.code,
          uniqueKey: `country-${country.code}`,
        }));
        setCountries(mappedCountries);
      } catch (error) {
        console.error("Error fetching countries:", error);
      }
    };
    if (searchTerm) {
      fetchData();
    }
  }, [searchTerm]);

  function checkCounterPartyType(countryCode: string) {
    const counterpartyType = "FIAT_" + countryCode;
    if (!allJurisdictions.includes(counterpartyType)) {
      setShowIsNotAvailableModal(true);
      setIsNotAvailable(true);
      return;
    } else {
      setIsNotAvailable(false);
    }
    if (ibanJurisdictions.includes(counterpartyType)) {
      dispatch(setForm({ isIbanAccount: true }));
      dispatch(setForm({ isClabeAccount: false }));
    } else if (clabeJurisdictions.includes(counterpartyType)) {
      dispatch(setForm({ isIbanAccount: false }));
      dispatch(setForm({ isClabeAccount: true }));
    } else if (accountNumberJurisdictions.includes(counterpartyType)) {
      dispatch(setForm({ isIbanAccount: false }));
      dispatch(setForm({ isClabeAccount: false }));
    }
  }

  const onSubmit = (data: FormData) => {
    const counterpartyType =
      withdrawalType === "CRYPTO" ? "CRYPTO" : "FIAT_" + selectedCountry;
    const railBasedOnCountry =
      counterpartyType === "FIAT_US" ? "FEDWIRE" : "SWIFT";
    const id = uuidv1();
    dispatch(setForm({ counterpartyType }));
    dispatch(
      setForm({
        rail: withdrawalType === "CRYPTO" ? "CRYPTO" : railBasedOnCountry,
      }),
    );
    dispatch(setForm({ currentTransactionId: String(id) }));
    reset();
    if (withdrawalType === "FIAT") {
      router.push("home/transaction/profile");
    } else {
      router.push("home/transaction/crypto/profile");
    }
  };

  const handleNationality = (data: { value: string; code: string }) => {
    setSelectedCountry(data.code);
    checkCounterPartyType(data.code);
    setValue("countryCode", data.code);
    setCountries([]);
  };

  const animatedStyle = useAnimatedStyle(() => {
    return {
      opacity: withTiming(fiatOptionsOpacity.value, {
        duration: 500,
        easing: Easing.linear,
      }),
    };
  });

  return (
    <ScrollView className="flex-grow items-center bg-white1 p-4">
      <Link href="/home/transaction/contacts" asChild>
        <View className="absolute top-0 left-0 m-1.5 z-50">
          <Ionicons name="chevron-back-outline" size={28} color="#BDBDBD" />
        </View>
      </Link>
      <Link href="/home" asChild>
        <View className="absolute top-0 right-0 m-1 z-50">
          <Ionicons name="close-outline" size={32} color="#BDBDBD" />
        </View>
      </Link>
      <View className="m-2">
        <Title text={t("home/transfer:title")} />
      </View>
      {/* <Subtitle text={t("home/transfer:subtitle")} /> */}
      <View className="w-full items-center">
        <TransferCartoon width={180} height={180} />
      </View>
      <View className="w-full items-center my-2">
        <Text className="text-base text-center font-light italic">
          WARNING: {t("common:modal.nonus.content")}
        </Text>
      </View>
      <View className="z-50 w-full items-center">
        <InputChoose
          name="withdrawalType"
          label={t("home/transfer:input.counterpartyType")}
          data={{ WithdrawalType }}
          handleChange={(data) => {
            setWithdrawalType(data);
            if (data === "FIAT") {
              fiatOptionsOpacity.value = 1;
            } else {
              fiatOptionsOpacity.value = 0;
            }
          }}
          control={control}
        />
      </View>
      <Animated.View
        style={animatedStyle}
        className="z-40 flex-grow w-full items-center"
      >
        <InputDropList
          label={t("home/transfer:input.countryofdestination")}
          handleChange={(data) => {
            handleNationality(data);
          }}
          clearValueFunction={() => {
            setSelectedCountry("");
            setCountries([]);
          }}
          data={countries}
          onSearchChange={(term) => setSearchTerm(term)}
          control={control}
          name="countryCode"
          placeholder={t("common:placeholder.country")}
        />
      </Animated.View>
      <View className="w-full items-center">
        <Button
          label={t("common:button.nextstep")}
          onClick={handleSubmit(onSubmit)}
          blocked={isNotAvailable}
        />
      </View>
      <WarningModal
        text={t("home/transfer:advice")}
        onClose={() => setShowIsNotAvailableModal(false)}
        onClick={() => setShowIsNotAvailableModal(false)}
        visible={showIsNotAvailableModal}
      />
    </ScrollView>
  );
}
